import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges, inject, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StoreService } from '../services/store.service';
import { PhotoboothService } from '../services/photobooth.service';
import { InvoiceService } from '../services/invoice.service';
import { PdfGeneratorService } from '../services/pdf-generator.service';
import { BrandService } from '../services/brand.service';
import { deviceBelongsToBrand } from '../services/device-brand.util';
import { firstValueFrom } from 'rxjs';

export interface MonthlyDeviceDetail {
  storeId: string;
  storeName: string;
  deviceId: string;
  deviceName: string;
  paper: number;
  card: { sales: number; overPayment: number; sum: number };
  cash: { sales: number; overPayment: number; sum: number };
  forced: { sales: number; overPayment: string; sum: string };
  coupon: { sales: number; overPayment: string; sum: string };
  groupSum: number;
}

@Component({
  selector: 'app-sales-report-modal',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './sales-report-modal.component.html',
  styleUrls: ['./sales-report-modal.component.css']
})
export class SalesReportModalComponent implements OnInit, OnChanges {
  private storeService = inject(StoreService);
  private photoboothService = inject(PhotoboothService);
  private invoiceService = inject(InvoiceService);
  private pdfService = inject(PdfGeneratorService);
  private brandService = inject(BrandService);
  private cd = inject(ChangeDetectorRef);

  @Input() isOpen = false;
  @Input() company: any = null;
  @Input() period: string = '';
  @Input() totalRevenue: number = 0;
  @Input() imageAttachments: any[] = [];
  @Input() imageUrl: string | null = null;
  @Input() showPdfActions: boolean = true;

  @Output() close = new EventEmitter<void>();

  isLoadingDetails = false;
  loadError = false;
  brandName = '';
  availableBranches: string[] = [];
  private loadSeq = 0;
  detailRows: MonthlyDeviceDetail[] = [];
  filteredDetailRows: MonthlyDeviceDetail[] = [];
  selectedBranchFilter: string = 'All';

  ngOnInit() {
    if (this.isOpen && this.company && this.period) {
      this.loadBreakdown();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if ((changes['isOpen']?.currentValue || changes['company']?.currentValue || changes['period']?.currentValue) && this.isOpen && this.company && this.period) {
      this.loadBreakdown();
    }
  }

  onClose() {
    this.close.emit();
  }

  /** Branch filter (store name or id). Totals, the table and the PDF all follow the filtered rows. */
  applyBranchFilter() {
    if (!this.selectedBranchFilter || this.selectedBranchFilter === 'All') {
      this.filteredDetailRows = [...this.detailRows];
      return;
    }
    const wanted = this.selectedBranchFilter.toLowerCase().trim();
    this.filteredDetailRows = this.detailRows.filter(r =>
      r.storeName.toLowerCase().trim() === wanted || r.storeId.toLowerCase().trim() === wanted);
  }

  /** The company's brand reference may be a brand document id or a brand name; devices carry the brand document id. */
  private async resolveBrand(brandRef: string): Promise<{ id: string; name: string }> {
    if (!brandRef) return { id: '', name: '' };
    try {
      const brands = await firstValueFrom(this.brandService.getBrands());
      const match = brands.find(b => b.id === brandRef || (b.name || '').trim() === brandRef.trim());
      return { id: match?.id || '', name: match?.name || '' };
    } catch {
      return { id: '', name: '' };
    }
  }

  get displayPeriod(): string {
    if (!this.period) return '';
    const parts = this.period.split('/');
    if (parts.length !== 2) return this.period;
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    const format = (d: Date) => `${(d.getMonth() + 1).toString().padStart(2, '0')}/${d.getDate().toString().padStart(2, '0')}/${d.getFullYear()}`;
    return `${format(startDate)}-${format(endDate)}`;
  }

  get dateRange(): { start: string; end: string; display: string } {
    if (!this.period) return { start: '', end: '', display: '' };
    const parts = this.period.split('/');
    if (parts.length !== 2) return { start: '', end: '', display: '' };
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10);
    const startDate = new Date(year, month - 1, 1);
    const endDate = new Date(year, month, 0);
    const pad = (n: number) => n.toString().padStart(2, '0');
    const startStr = `${pad(startDate.getMonth() + 1)}.${pad(startDate.getDate())}.${startDate.getFullYear()}`;
    const endStr = `${pad(endDate.getMonth() + 1)}.${pad(endDate.getDate())}.${endDate.getFullYear()}`;
    return {
      start: startStr,
      end: endStr,
      display: `${startStr} ~ ${endStr}`
    };
  }

  get totals() {
    let totalPaper = 0;
    let totalSales = 0;
    let totalGroupSum = 0;

    this.filteredDetailRows.forEach(row => {
      totalPaper += (row.paper || 0);
      const cardSales = row.card?.sales || 0;
      const cashSales = row.cash?.sales || 0;
      totalSales += (cardSales + cashSales);
      totalGroupSum += (row.groupSum || 0);
    });

    return {
      paper: totalPaper,
      sales: totalSales,
      groupSum: totalGroupSum
    };
  }

  async loadBreakdown() {
    if (!this.company || !this.period) return;
    const seq = ++this.loadSeq;
    this.isLoadingDetails = true;
    this.loadError = false;
    this.detailRows = [];
    this.filteredDetailRows = [];
    this.availableBranches = [];
    this.selectedBranchFilter = 'All';

    try {
      // Only this company's brand's devices are listed: a store can host devices of several brands.
      const brand = await this.resolveBrand((this.company.brand || '').toString().trim());
      this.brandName = brand.name;

      const parts = this.period.split('/');
      if (parts.length !== 2) return;
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10);
      const startDate = new Date(year, month - 1, 1);
      const endDate = new Date(year, month, 0);
      const pad = (n: number) => n.toString().padStart(2, '0');
      const startDateStr = `${year}-${pad(month)}-01`;
      const endDateStr = `${year}-${pad(month)}-${pad(endDate.getDate())}`;

      const companyId = this.company.id;

      // 1. Get stores for company
      const stores = await this.storeService.getStoresByCompanyId(companyId);
      const allStoresSnap = await this.storeService.getStoresSnapshot();
      const allStores: any[] = allStoresSnap.docs.map(d => ({ id: d.id, ...d.data() }));
      const companyStores = allStores.filter((s: any) => {
        const cids = s['companyIds'] || (s['companyId'] ? [s['companyId']] : []);
        return cids.includes(companyId) || s['company'] === this.company.company;
      });

      const storeMap = new Map<string, any>();
      stores.forEach(s => storeMap.set(s.storeId || s.id, s));
      companyStores.forEach(s => storeMap.set(s.storeId || s.id, s));

      const companyStoreList = Array.from(storeMap.values());
      const storeDocIds = companyStoreList.map(s => s.id).filter(Boolean);
      const storeCodeIds = companyStoreList.map(s => s.storeId).filter(Boolean);
      const allStoreIdentifiers = new Set([...storeDocIds, ...storeCodeIds].map(s => s.toString().trim().toLowerCase()));

      // 2. Fetch device data for date range
      const allDevicesData = await this.photoboothService.getAllDevicesDateRangeRevenue(startDateStr, endDateStr);

      const detailRows: MonthlyDeviceDetail[] = [];
      const addedDeviceIds = new Set<string>();

      for (const store of companyStoreList) {
        const storeDevices = (await this.storeService.getDevices(store.id || '', store.storeId))
          .filter((d: any) => deviceBelongsToBrand(d, brand.id, brand.name));
        for (const sDev of storeDevices) {
          const sDevId = sDev.deviceId || sDev.id;
          if (!sDevId || addedDeviceIds.has(sDevId)) continue;
          addedDeviceIds.add(sDevId);

          const matchedDev = allDevicesData.find(d => d.deviceId === sDevId);
          let data = matchedDev?.data || {
            CardTotal: 0,
            CashTotal: 0,
            CouponTotal: 0,
            DailyForcedAmount: 0,
            DailyOverpaidAmount: 0,
            DailyRevenue: 0,
            DailySessions: 0
          };
          const deviceData = { ...sDev, ...(matchedDev?.deviceData || {}) };

          if (!data.CardTotal && !data.CashTotal && !data.CouponTotal) {
            try {
              const monthlyRep = await this.photoboothService.getMonthlyReport(sDevId, this.period.replace('/', '-')) ||
                                 await this.photoboothService.getMonthlyReport(sDevId, this.period);
              if (monthlyRep) {
                data = {
                  CardTotal: Number(monthlyRep['CardTotal'] || monthlyRep['cardTotal'] || monthlyRep['credit'] || 0),
                  CashTotal: Number(monthlyRep['CashTotal'] || monthlyRep['cashTotal'] || monthlyRep['cash'] || 0),
                  CouponTotal: Number(monthlyRep['CouponTotal'] || monthlyRep['couponTotal'] || monthlyRep['coupon'] || 0),
                  DailyForcedAmount: Number(monthlyRep['DailyForcedAmount'] || monthlyRep['forcedTotal'] || monthlyRep['forced'] || 0),
                  DailyOverpaidAmount: Number(monthlyRep['TotalOverpaidAmount'] || monthlyRep['OverpaidAmount'] || monthlyRep['overpaidAmount'] || monthlyRep['overCharge'] || 0),
                  DailyRevenue: Number(monthlyRep['DailyRevenue'] || 0),
                  DailySessions: Number(monthlyRep['DailySessions'] || monthlyRep['dailySessions'] || monthlyRep['totalSession'] || 0)
                };
              }
            } catch (err) {
              console.warn(`Could not fetch fallback monthly report for ${sDevId}:`, err);
            }
          }

          const storeId = store.storeId || store.id;
          const storeName = store.name || store.storeId || store.id;
          const deviceName = deviceData.deviceName || deviceData.name || sDevId;

          const cardSales = Number(data.CardTotal || data.cardTotal || data.credit || 0);
          const cashSales = Number(data.CashTotal || data.cashTotal || data.cash || 0);
          const couponSales = Number(data.CouponTotal || data.couponTotal || data.coupon || 0);
          const forcedSales = Number(data.DailyForcedAmount || data.forcedTotal || data.forced || 0);
          const overPayment = Number(data.DailyOverpaidAmount || data.overpaidAmount || data.overCharge || 0);
          const paper = Number(deviceData.printsLeft ?? deviceData.remainingPapers ?? 0);

          const cardSum = cardSales;
          const cashSum = cashSales;
          const groupSum = cardSales + cashSales;

          detailRows.push({
            storeId: storeId,
            storeName: storeName,
            deviceId: sDevId,
            deviceName: deviceName,
            paper: paper,
            card: { sales: cardSales, overPayment: 0, sum: cardSum },
            cash: { sales: cashSales, overPayment: 0, sum: cashSum },
            forced: { sales: 0, overPayment: '.', sum: '.' },
            coupon: { sales: 0, overPayment: '.', sum: '.' },
            groupSum: groupSum
          });
        }
      }

      // Also include devices matching store identifiers that weren't in getDevices
      const companyDevicesData = allDevicesData.filter((d: any) => {
        const devStoreId = (d.deviceData?.storeId || '').toString().trim().toLowerCase();
        const devStoreDocId = (d.deviceData?.storeDocId || '').toString().trim().toLowerCase();
        return (allStoreIdentifiers.has(devStoreId) || allStoreIdentifiers.has(devStoreDocId))
          && deviceBelongsToBrand(d.deviceData, brand.id, brand.name);
      });

      for (const dev of companyDevicesData) {
        if (addedDeviceIds.has(dev.deviceId)) continue;
        addedDeviceIds.add(dev.deviceId);

        const data = dev.data || {};
        const deviceId = dev.deviceId;
        const deviceData = dev.deviceData || {};
        const devStore = companyStoreList.find(s => 
          s.id === deviceData.storeDocId || 
          s.storeId === deviceData.storeId || 
          s.id === deviceData.storeId
        );
        const storeId = devStore?.storeId || deviceData.storeId || 'Unknown';
        const storeName = devStore?.name || deviceData.storeName || storeId;
        const deviceName = deviceData.deviceName || deviceData.name || deviceId;

        const cardSales = Number(data.CardTotal || data.cardTotal || data.credit || 0);
        const cashSales = Number(data.CashTotal || data.cashTotal || data.cash || 0);
        const couponSales = Number(data.CouponTotal || data.couponTotal || data.coupon || 0);
        const forcedSales = Number(data.DailyForcedAmount || data.forcedTotal || data.forced || 0);
        const overPayment = Number(data.DailyOverpaidAmount || data.overpaidAmount || data.overCharge || 0);
        const paper = Number(deviceData.printsLeft ?? deviceData.remainingPapers ?? 0);

        const cardSum = cardSales;
        const cashSum = cashSales;
        const groupSum = cardSales + cashSales;

        detailRows.push({
          storeId: storeId,
          storeName: storeName,
          deviceId: deviceId,
          deviceName: deviceName,
          paper: paper,
          card: { sales: cardSales, overPayment: 0, sum: cardSum },
          cash: { sales: cashSales, overPayment: 0, sum: cashSum },
          forced: { sales: 0, overPayment: '.', sum: '.' },
          coupon: { sales: 0, overPayment: '.', sum: '.' },
          groupSum: groupSum
        });
      }

      if (seq !== this.loadSeq) return; // a newer company/period was opened meanwhile
      this.detailRows = detailRows;
      const branches = new Set<string>();
      companyStoreList.forEach(s => branches.add(s.name || s.storeId || s.id));
      this.availableBranches = Array.from(branches).filter(Boolean).sort((a, b) => a.localeCompare(b));
      this.applyBranchFilter();
    } catch (error) {
      console.error('Error loading sales report breakdown:', error);
      if (seq === this.loadSeq) {
        this.loadError = true;
        this.detailRows = [];
        this.filteredDetailRows = [];
      }
    } finally {
      if (seq === this.loadSeq) {
        this.isLoadingDetails = false;
        this.cd.detectChanges();
      }
    }
  }

  async printReport() {
    if (!this.company) return;
    await this.handlePdfAction('open');
  }

  async saveReportPdf() {
    if (!this.company) return;
    await this.handlePdfAction('save');
  }

  private async handlePdfAction(action: 'save' | 'open'): Promise<void> {
    let pdfWindow: Window | null = null;
    if (action === 'open') {
      pdfWindow = window.open('', '_blank');
      if (pdfWindow) {
        pdfWindow.document.write('<html><body><div style="display:flex;justify-content:center;align-items:center;height:100vh;font-family:sans-serif;"><div>Generating PDF... Please wait.</div></div></body></html>');
      }
    }

    this.cd.detectChanges();
    await new Promise(resolve => setTimeout(resolve, 1000));

    const content = document.getElementById('sales-pdf-content-modal');
    if (content) {
      const filename = `Sales_Report_${(this.company.company || '').replace(/\\s+/g, '_')}_${this.period.replace('/', '-')}.pdf`;
      try {
        if (action === 'save') {
          await this.pdfService.savePdf(content, filename, 'portrait');
        } else if (action === 'open') {
          await this.pdfService.openPdf(content, filename, 'portrait', pdfWindow || undefined);
        }
      } catch (error) {
        if (pdfWindow) pdfWindow.close();
        console.error(error);
      }
    }
  }
}
